import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "@/store/AppContext";
import { http } from "@/lib/api";
import { COUNTRY_DETAILS } from "@/lib/countryDetails";
import { Briefcase, GraduationCap, Award, HelpCircle } from "lucide-react";

const DESTS = [
  { city: "Toronto",   countryKey: "Canada",         flag: "🇨🇦", img: "https://images.unsplash.com/photo-1543962226-818f4301073f?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { city: "London",    countryKey: "United Kingdom", flag: "🇬🇧", img: "https://images.unsplash.com/photo-1520986606214-8b456906c813?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { city: "New York",  countryKey: "USA",            flag: "🇺🇸", img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { city: "Sydney",    countryKey: "Australia",      flag: "🇦🇺", img: "https://images.unsplash.com/photo-1523059623039-a9ed027e7fad?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { city: "Berlin",    countryKey: "Germany",        flag: "🇩🇪", img: "https://images.unsplash.com/photo-1560969184-10fe8719e047?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { city: "Dubai",     countryKey: "UAE",            flag: "🇦🇪", img: "https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
  { city: "Singapore", countryKey: "Singapore",      flag: "🇸🇬", img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" },
];

const KIND_META = {
  Work:  { Icon: Briefcase,     cls: "bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/40" },
  Study: { Icon: GraduationCap, cls: "bg-sky-500/15 text-sky-200 border-sky-400/40" },
  PR:    { Icon: Award,         cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40" },
};

export default function Destinations() {
  const { openContact } = useApp();
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    http.get("/blog/posts").then(({ data }) => {
      const items = (data.posts || []).filter((p) => p.category === "FAQ").slice(0, 24);
      setFaqs(items);
    }).catch(() => {});
  }, []);

  // Pick FAQs per country by naive title match
  const faqByCountry = useMemo(() => {
    const m = {};
    DESTS.forEach((d) => {
      m[d.countryKey] = faqs
        .filter((f) => f.title.toLowerCase().includes(d.countryKey.toLowerCase()) || (f.excerpt || "").toLowerCase().includes(d.countryKey.toLowerCase()))
        .slice(0, 2);
    });
    // Fallback: if a country has none, show the first generic FAQ
    const fallback = faqs[0];
    DESTS.forEach((d) => { if (!m[d.countryKey].length && fallback) m[d.countryKey] = [fallback]; });
    return m;
  }, [faqs]);

  return (
    <section id="destinations" data-testid="destinations-section" className="py-24 bg-app-alt">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C9A961] font-semibold">Destinations</span>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight text-app">
              Seven shores. <em className="text-[#C9A961]">Endless futures.</em>
            </h2>
          </div>
          <p className="text-sm text-app-muted max-w-sm">
            Hover a card to reveal Work · Study · PR routes with timeline, cost and matching FAQ.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {DESTS.map((d, idx) => {
            const detail = COUNTRY_DETAILS[d.countryKey];
            const kFaq = faqByCountry[d.countryKey] || [];
            const isLastOrphan = idx === DESTS.length - 1 && DESTS.length % 3 === 1;
            return (
              <div
                key={d.city}
                data-testid={`destination-card-${d.city.toLowerCase()}`}
                className={`group relative rounded-3xl overflow-hidden text-left bg-[#0B1E3F] shadow-sm ${isLastOrphan ? "lg:col-start-2" : ""}`}
              >
                <img
                  src={d.img}
                  alt={d.city}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-85"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E3F] via-[#0B1E3F]/70 to-[#0B1E3F]/20" />

                <div className="relative p-6 flex flex-col justify-between min-h-[400px]">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{d.flag}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-[#C9A961]">{d.countryKey}</span>
                    </div>
                    <h3 className="font-serif text-3xl text-white">{d.city}</h3>
                    <p className="text-[11px] text-slate-300 mt-2 uppercase tracking-widest opacity-80 group-hover:opacity-0 transition-opacity duration-300">
                      Hover for Work · Study · PR
                    </p>
                  </div>

                  {/* Route chips (revealed on hover) */}
                  <div className="mt-6 max-h-0 group-hover:max-h-[260px] overflow-hidden transition-all duration-500 opacity-0 group-hover:opacity-100 space-y-2">
                    {detail?.routes?.map((r) => {
                      const meta = KIND_META[r.kind] || KIND_META.Work;
                      const Icon = meta.Icon;
                      return (
                        <div
                          key={r.kind}
                          data-testid={`dest-route-${d.city.toLowerCase()}-${r.kind.toLowerCase()}`}
                          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs backdrop-blur-sm ${meta.cls}`}
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate text-white/95">{r.name}</div>
                            <div className="text-[10px] opacity-80">{r.time} · {r.cost}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* FAQ inline (slides open on hover) */}
                  {kFaq.length > 0 && (
                    <div className="mt-4 max-h-0 group-hover:max-h-56 overflow-hidden transition-all duration-500 opacity-0 group-hover:opacity-100">
                      <div className="border-t border-[#C9A961]/30 pt-3 space-y-2">
                        {kFaq.map((f) => (
                          <a
                            key={f.id}
                            href={`/blog/${f.slug}`}
                            data-testid={`dest-faq-${d.city.toLowerCase()}-${f.slug}`}
                            className="flex items-start gap-2 text-xs text-slate-200 hover:text-white"
                          >
                            <HelpCircle className="w-3 h-3 mt-0.5 shrink-0 text-[#C9A961]" />
                            <span className="line-clamp-2">{f.title}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => openContact({ target_country: d.countryKey })}
                    data-testid={`dest-apply-${d.city.toLowerCase()}`}
                    className="mt-4 w-full rounded-full py-2.5 text-sm font-semibold bg-[#C9A961] text-[#0B1E3F] hover:bg-[#D7B974] transition inline-flex items-center justify-center gap-2"
                  >
                    Start my {d.countryKey} application →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
